import React, { useEffect, useMemo, useState } from "react";
import { X, Search, AlertTriangle, AlertCircle, CheckCircle2, Target, Lightbulb } from "lucide-react";
import { fetchAiSummary } from "../lib/api";
import { titleCase, formatDateID } from "../lib/format";

const URGENCY_STYLE = {
  kritis: { label: "Kritis", color: "#ef4444", bg: "rgba(239, 68, 68, 0.12)" },
  sangat_tinggi: { label: "Sangat Tinggi", color: "#f87171", bg: "rgba(248, 113, 113, 0.12)" },
  tinggi: { label: "Tinggi", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.12)" },
  sedang: { label: "Sedang", color: "#facc15", bg: "rgba(250, 204, 21, 0.12)" },
  rendah: { label: "Rendah", color: "#10b981", bg: "rgba(16, 185, 129, 0.12)" },
};

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, "_");

export const AnalisisRekomendasiModal = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("ALL");
  const [searchQ, setSearchQ] = useState("");
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    fetchAiSummary()
      .then((d) => {
        if (mounted && d?.success) setItems(d.data || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [open]);

  const counts = useMemo(() => {
    const c = { tinggi: 0, sedang: 0, rendah: 0 };
    items.forEach((it) => {
      const key = norm(it.significance_scale);
      if (key === "kritis" || key === "sangat_tinggi" || key === "tinggi") c.tinggi += 1;
      else if (key === "sedang") c.sedang += 1;
      else c.rendah += 1;
    });
    return c;
  }, [items]);

  const regionList = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const name = (it.region_name || "NASIONAL").toUpperCase();
      map.set(name, (map.get(name) || 0) + 1);
    });
    return [["SEMUA WILAYAH", items.length], ...Array.from(map.entries()).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const nameUp = (it.region_name || "").toUpperCase();
      const wilayahOK =
        selectedRegion === "ALL" ||
        selectedRegion === "SEMUA WILAYAH" ||
        nameUp === selectedRegion;
      const q = searchQ.trim().toLowerCase();
      const searchOK =
        !q ||
        (it.issue_title || "").toLowerCase().includes(q) ||
        (it.issue_summary || "").toLowerCase().includes(q) ||
        nameUp.toLowerCase().includes(q);
      return wilayahOK && searchOK;
    });
  }, [items, selectedRegion, searchQ]);

  if (!open) return null;

  return (
    <div className="nms-modal-backdrop" data-testid="analisis-modal" onClick={onClose}>
      <div className="nms-modal analisis-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} data-testid="analisis-modal-close" aria-label="Close">
          <X size={18} />
        </button>

        <header className="anal-head">
          <div>
            <h2 className="anal-title">Rekomendasi Strategis</h2>
            <p className="anal-sub">
              Berdasarkan ringkasan sentimen dan pantauan isu terkini
            </p>
          </div>
          <div className="anal-stats">
            <div className="anal-stat" data-testid="anal-stat-tinggi">
              <AlertTriangle size={22} className="ico" style={{ color: "#ef4444" }} />
              <div className="num">{counts.tinggi}</div>
              <div className="lbl" style={{ color: "#ef4444" }}>TINGGI</div>
            </div>
            <div className="anal-stat" data-testid="anal-stat-sedang">
              <AlertCircle size={22} className="ico" style={{ color: "#f59e0b" }} />
              <div className="num">{counts.sedang}</div>
              <div className="lbl" style={{ color: "#f59e0b" }}>SEDANG</div>
            </div>
            <div className="anal-stat" data-testid="anal-stat-rendah">
              <CheckCircle2 size={22} className="ico" style={{ color: "#10b981" }} />
              <div className="num">{counts.rendah}</div>
              <div className="lbl" style={{ color: "#10b981" }}>RENDAH</div>
            </div>
          </div>
        </header>

        <div className="anal-body">
          <aside className="anal-filter">
            <div className="af-title">Filter Wilayah</div>
            <input
              type="date"
              className="af-date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              data-testid="analisis-date-input"
            />
            <div className="af-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Cari provinsi..."
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                data-testid="analisis-search-input"
              />
            </div>
            <ul className="af-list">
              {regionList.map(([name, count]) => {
                const active =
                  (selectedRegion === "ALL" && name === "SEMUA WILAYAH") ||
                  selectedRegion === name;
                return (
                  <li key={name}>
                    <button
                      type="button"
                      className={`af-item ${active ? "active" : ""}`}
                      onClick={() =>
                        setSelectedRegion(name === "SEMUA WILAYAH" ? "ALL" : name)
                      }
                      data-testid={`region-${name.replace(/\s+/g, "_")}`}
                    >
                      <span>{titleCase(name)}</span>
                      <span className="af-count">{count}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="anal-list" data-testid="anal-list">
            {loading && <div className="anal-loading">Memuat rekomendasi…</div>}
            {!loading && filtered.length === 0 && (
              <div className="anal-loading">Tidak ada rekomendasi untuk filter saat ini.</div>
            )}
            {filtered.map((it, i) => {
              const urgKey = norm(it.significance_scale) || "sedang";
              const urg = URGENCY_STYLE[urgKey] || URGENCY_STYLE.sedang;
              return (
                <article key={i} className="anal-item" data-testid={`anal-item-${i}`}>
                  <div className="ai-head">
                    <div className="ai-tags">
                      <span className="ai-region">{titleCase(it.region_name || "Nasional")}</span>
                      <span className="ai-date">{formatDateID(it.date)}</span>
                    </div>
                    <span
                      className="ai-urgency"
                      style={{ color: urg.color, borderColor: urg.color, background: urg.bg }}
                    >
                      <div className="ai-urgency-label">URGENSI</div>
                      <div className="ai-urgency-val">{urg.label}</div>
                    </span>
                  </div>
                  <h3 className="ai-title">{it.issue_title}</h3>
                  <p className="ai-summary">{it.issue_summary}</p>

                  {it.impact?.description && (
                    <div className="ai-impact">
                      <div className="ai-impact-head">
                        <Target size={13} style={{ color: "#ef4444" }} />
                        <span>Dampak Potensial</span>
                      </div>
                      <p>{it.impact.description}</p>
                    </div>
                  )}

                  {Array.isArray(it.recommendation) && it.recommendation.length > 0 && (
                    <div className="ai-rec">
                      <div className="ai-rec-head">
                        <Lightbulb size={13} style={{ color: "#3b82f6" }} />
                        <span>Langkah Strategis</span>
                      </div>
                      <ul>
                        {it.recommendation.map((r, j) => (
                          <li key={j}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AnalisisRekomendasiModal;
